import { create } from "zustand";

import { dayjs } from "@av/config/extended-dayjs";
import { addCandidateToJobHelper } from "./add-candidate-to-job";
import {
  type LifecycleStatus,
  type MockApplicantData,
  type MockApplicationNoteData,
  type PipelineStage,
} from "./candidate-pool.types";
import { prepareDB } from "./prepare-db";
import { type MockJobPostData } from "../types";

import type { MockUser } from "./collections/users";

export type MockDbState = {
  users: MockUser[];
  jobPosts: Partial<MockJobPostData>[];
  reset: () => void;
  addJobPost: (jobPost: Partial<MockJobPostData>) => void;

  candidates: Partial<MockApplicantData>[];
  applicationNotes: MockApplicationNoteData[];
  addExternalCandidateToJob: (jobPostId: string, candidate: Partial<MockApplicantData>) => void;
  addCandidateToJobPost: (jobPostId: string, candidate: Partial<MockApplicantData>) => void;
  updateCandidatesStatus: (
    applicationIds: string[],
    status: { status: LifecycleStatus; pipelineStage: PipelineStage | null }
  ) => void;
  resolveCandidates: (
    applicationIds: string[],
    status: Extract<LifecycleStatus, "HIRED" | "REJECTED" | "WITHDRAWN">
  ) => void;
  reconsiderCandidates: (applicationIds: string[]) => void;
  addApplicationNote: (note: MockApplicationNoteData) => void;
  updateApplicationNote: (applicationNoteId: string, body: string, updatedAt: string) => void;
  deleteApplicationNote: (applicationNoteId: string) => void;
};

export const useMockDbStore = create<MockDbState>()(function initializeStore(set) {
  return {
    ...prepareDB(),
    reset: () => set(() => prepareDB()),
    addJobPost: (jobPost) =>
      set((state) => ({
        jobPosts: [...state.jobPosts, jobPost],
      })),
    addExternalCandidateToJob: (jobPostId: string, candidate: Partial<MockApplicantData>) => {
      set((state) => {
        const jobPost = state.jobPosts.find((j) => j.jobPostId === jobPostId);

        if (!jobPost) {
          throw new Error("Invalid job id");
        }

        const newState = addCandidateToJobHelper({
          jobPostId,
          candidate,
          state,
          additionalCandidateProps: {
            source: "EXTERNAL",
            accessIds: (jobPost.recruiters ?? []).map((user) => user.userId),
            status: "APPLIED",
            pipelineStage: null,
            stageEnteredAt: dayjs().toISOString(),
            name: [candidate.firstName, candidate.middleName, candidate.lastName]
              .filter(Boolean)
              .join(" "),
          },
        });

        return newState;
      });
    },
    addCandidateToJobPost: (jobPostId: string, candidate: Partial<MockApplicantData>) => {
      set((state) => {
        const newState = addCandidateToJobHelper({
          jobPostId,
          candidate,
          state,
          additionalCandidateProps: {
            source: "INTERNAL",
            status: "APPLIED",
            pipelineStage: null,
            stageEnteredAt: dayjs().toISOString(),
            name: [candidate.firstName, candidate.middleName, candidate.lastName]
              .filter(Boolean)
              .join(" "),
          },
        });

        return newState;
      });
    },
    updateCandidatesStatus: (applicationIds, status) => {
      set((state) => ({
        candidates: state.candidates.map((candidate) =>
          candidate.applicationId && applicationIds.includes(candidate.applicationId)
            ? {
                ...candidate,
                status: status.status,
                pipelineStage: status.pipelineStage,
                stageEnteredAt: dayjs().toISOString(),
              }
            : candidate
        ),
      }));
    },
    reconsiderCandidates: (applicationIds) => {
      set((state) => ({
        candidates: state.candidates.map((candidate) =>
          candidate.applicationId && applicationIds.includes(candidate.applicationId)
            ? {
                ...candidate,
                status: candidate.pipelineStage ? "IN_PIPELINE" : "APPLIED",
                stageEnteredAt: dayjs().toISOString(),
              }
            : candidate
        ),
      }));
    },
    resolveCandidates: (applicationIds, status) => {
      set((state) => ({
        candidates: state.candidates.map((candidate) =>
          candidate.applicationId && applicationIds.includes(candidate.applicationId)
            ? { ...candidate, status, stageEnteredAt: dayjs().toISOString() }
            : candidate
        ),
      }));
    },
    addApplicationNote: (note) =>
      set((state) => ({
        applicationNotes: [note, ...state.applicationNotes],
      })),
    updateApplicationNote: (applicationNoteId, body, updatedAt) =>
      set((state) => ({
        applicationNotes: state.applicationNotes.map((note) =>
          note.applicationNoteId === applicationNoteId
            ? {
                ...note,
                body,
                updatedAt,
              }
            : note
        ),
      })),
    deleteApplicationNote: (applicationNoteId) =>
      set((state) => ({
        applicationNotes: state.applicationNotes.filter(
          (note) => note.applicationNoteId !== applicationNoteId
        ),
      })),
  };
});

export function useDBSingleJobPost(jobPostId: string) {
  const jobPost = useMockDbStore((state) => state.jobPosts.find((j) => j.jobPostId === jobPostId));

  return jobPost;
}

export const getMockDbJobPosts = () => useMockDbStore.getState().jobPosts;
export const getMockDbCandidatePool = () => useMockDbStore.getState().candidates;
